import { getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, updateDoc, arrayUnion, deleteDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { auth, db, messaging, vapidKey } from './firebase';

// The app's single service worker (public/sw.js) handles BOTH caching and push
// notifications. A browser allows only one SW per scope, so push must live in
// the same file the app already registers for offline caching.
const SW_PATH = '/sw.js';

const isSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

let foregroundHandlerReady = false;

// Register the push service worker (must live at the app root scope).
export async function registerPushServiceWorker() {
  if (!isSupported()) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH);
  } catch (err) {
    console.warn('Push service worker registration failed:', err);
    return null;
  }
}

// Foreground messages (app open & focused) render as in-app toasts.
function setupForegroundHandler() {
  if (foregroundHandlerReady) return;
  foregroundHandlerReady = true;
  onMessage(messaging, (payload) => {
    // Prefer the `data` block (always delivered) over `notification`.
    const data = payload?.data || {};
    const notification = payload?.notification || {};
    const title = data.title || notification.title || 'New appointment';
    const body = data.body || notification.body || '';
    toast(body ? `${title} — ${body}` : title, { duration: 5000 });
  });
}

// Ask for permission, mint an FCM token, and store it under the user's UID.
// Every failure path surfaces a toast so the toggle is self-diagnosing.
export async function enablePushNotifications({ silent = false } = {}) {
  const fail = (msg) => {
    if (!silent) toast(msg);
    return false;
  };

  if (!isSupported()) {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    return fail(
      isIOS
        ? 'Push isn’t available in Safari tabs. On iPhone: add the app to your Home Screen, then open it from there (requires iOS 16.4+).'
        : 'Push notifications aren’t supported in this browser.'
    );
  }
  const user = auth.currentUser;
  if (!user) return fail('Sign in first to enable push notifications.');

  if (!vapidKey) {
    return fail('Push notifications need a VAPID key (set REACT_APP_FIREBASE_VAPID_KEY).');
  }

  let permission = Notification.permission;
  if (permission === 'default') {
    try {
      permission = await Notification.requestPermission();
    } catch {
      return fail('Could not ask for notification permission.');
    }
  }
  if (permission === 'denied') {
    return fail('Notifications are blocked in your browser settings. Allow notifications for this site, then try again.');
  }
  if (permission !== 'granted') {
    return fail('Notification permission was not granted.');
  }

  const registration = await registerPushServiceWorker();
  if (!registration) return fail('Could not register the notification service worker. Try a hard refresh.');

  setupForegroundHandler();

  try {
    const currentToken = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (!currentToken) {
      return fail('Could not get a push token from Firebase. Make sure FCM is enabled for this project.');
    }

    const tokenDocRef = doc(db, 'pushTokens', user.uid);
    const email = user.email || '';
    const updatedAt = new Date().toISOString();
    // Client reads on pushTokens are denied by Firestore rules (server-only),
    // so append via arrayUnion (no read needed) instead of read-then-merge.
    try {
      await updateDoc(tokenDocRef, { tokens: arrayUnion(currentToken), email, updatedAt });
    } catch (err) {
      if (err?.code === 'not-found') {
        await setDoc(tokenDocRef, { tokens: [currentToken], email, updatedAt });
      } else {
        throw err;
      }
    }
    localStorage.setItem(enabledFlag(user.uid), '1');
    return true;
  } catch (err) {
    console.warn('Failed to get or save push token:', err);
    const code = err?.code || '';
    if (code === 'messaging/unsupported-browser') {
      return fail('This browser doesn’t support web push. On iPhone, add the site to your Home Screen and open it from there (iOS 16.4+).');
    }
    if (code === 'messaging/invalid-vapid-key') return fail('The VAPID key is invalid.');
    if (code === 'messaging/token-subscribe-failed') return fail('Couldn’t subscribe to push — check your network and try again.');
    return fail(`Couldn’t enable push: ${err?.message || 'unknown error'}`);
  }
}

// Remove this user's token doc (single owner/device; the cleanest option).
export async function disablePushNotifications() {
  if (!isSupported()) return;
  const user = auth.currentUser;
  if (!user) return;
  try {
    await deleteDoc(doc(db, 'pushTokens', user.uid));
  } catch (err) {
    console.warn('Failed to remove push token:', err);
  } finally {
    localStorage.removeItem(enabledFlag(user.uid));
  }
}

export async function isPushEnabled() {
  if (!isSupported()) return false;
  const user = auth.currentUser;
  if (!user) return false;
  // Client reads on pushTokens are denied by Firestore rules; the client
  // tracks its own per-device enabled state in localStorage instead.
  return localStorage.getItem(enabledFlag(user.uid)) === '1';
}

const enabledFlag = (uid) => `push-enabled:${uid}`;

// Called once after sign-in: keep the SW alive, attach the foreground handler,
// and re-save the token if permission was already granted (no re-prompt).
export async function initPushNotifications() {
  if (!isSupported()) return;
  const user = auth.currentUser;
  if (!user) return;
  await registerPushServiceWorker();
  setupForegroundHandler();
  if (Notification.permission === 'granted') {
    await enablePushNotifications({ silent: true });
  }
}
